import './QBranchPersonnelDrawer.css';
import type {
  PersonnelManifestEntry,
  PersonnelCategory,
} from '../../services/host/QBranchPersonnelServiceModule';
import type {
  QBranchPersonnelLibraryState,
  QBranchPersonnelLibraryTab,
} from '../../hooks/useQBranchPersonnelLibrary';

/**
 * QBranchPersonnelDrawer — 2026-06-08 Phase 5E for Q Manual §18 dossiers.
 *
 * 3-tab layout honoring the Q Manual's narrative factional structure:
 * Leadership (1 — Q) / Harrow Club (4) / Newer Member (1 — Turner).
 *
 * Per-dossier detail expansion: stats grid + skills table + abilities +
 * physical sub-block + fame + hero points + fields of experience + 4
 * prose paragraphs (background / personality / relationships / game info).
 */

const TAB_DEFS: ReadonlyArray<{ key: QBranchPersonnelLibraryTab; label: string }> = [
  { key: 'leadership', label: 'Leadership' },
  { key: 'harrow_club', label: 'Harrow Club' },
  { key: 'newer_member', label: 'Newer Member' },
];

const TAB_TO_CATEGORY: Record<QBranchPersonnelLibraryTab, PersonnelCategory> = {
  leadership: 'leadership',
  harrow_club: 'harrow_club',
  newer_member: 'newer_member',
};

const STAT_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'STR', label: 'STR' },
  { key: 'DEX', label: 'DEX' },
  { key: 'WIL', label: 'WIL' },
  { key: 'PER', label: 'PER' },
  { key: 'INT', label: 'INT' },
];

const PHYSICAL_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'height', label: 'Height' },
  { key: 'weight', label: 'Weight' },
  { key: 'hair', label: 'Hair' },
  { key: 'eyes', label: 'Eyes' },
  { key: 'appearance', label: 'Appearance' },
  { key: 'hand_to_hand_damage_class', label: 'H2H DC' },
  { key: 'stamina', label: 'Stamina' },
  { key: 'running_swimming', label: 'Run/Swim' },
  { key: 'carrying', label: 'Carrying' },
  { key: 'speed', label: 'Speed' },
];

export interface QBranchPersonnelDrawerProps {
  personnel: PersonnelManifestEntry[];
  state: QBranchPersonnelLibraryState;
  error?: string | null;
  activeTab: QBranchPersonnelLibraryTab;
  onTabChange: (tab: QBranchPersonnelLibraryTab) => void;
  selectedPersonnelId: string | null;
  selectedPersonnelData: Record<string, unknown> | null;
  selectedPersonnelState: QBranchPersonnelLibraryState;
  onSelectPersonnel: (id: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

function PersonnelDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const stats = (data['stats'] ?? {}) as Record<string, unknown>;
  const skills = (data['skills'] ?? {}) as Record<
    string,
    { skill_level: number; primary_chance: number }
  >;
  const abilities = (data['abilities'] ?? []) as string[];
  const physical = (data['physical'] ?? {}) as Record<string, unknown>;
  const fields = (data['fields_of_experience'] ?? []) as string[];
  const famePoints = data['fame_points'] as number | undefined;
  const heroPoints = data['hero_points'] as number | undefined;
  const background = (data['background'] as string) ?? '';
  const personality = (data['personality'] as string) ?? '';
  const relationships = (data['relationships'] as string) ?? '';
  const gameInformation = (data['game_information'] as string) ?? '';
  const page = data['page'] as number | undefined;
  const codeName = data['code_name'] as string | null | undefined;
  const rank = data['rank'] as string | null | undefined;
  const role = data['role'] as string | undefined;
  const birthdate = data['birthdate'] as string | undefined;
  const birthplace = data['birthplace'] as string | undefined;

  return (
    <div
      className="br-personnel-detail"
      data-testid={`${testidPrefix}-detail`}
    >
      <h3 className="br-personnel-detail-title">
        {(data['name'] as string) ?? '—'}
        {codeName && (
          <span className="br-personnel-detail-codename"> ({codeName})</span>
        )}
      </h3>

      {role && (
        <p
          className="br-personnel-detail-role"
          data-testid={`${testidPrefix}-detail-role`}
        >
          {role}
        </p>
      )}

      <p
        className="br-personnel-detail-meta br-label"
        data-testid={`${testidPrefix}-detail-meta`}
      >
        {rank && <>{rank} · </>}
        {page !== undefined && <>Q Manual p.{page}</>}
        {(birthdate || birthplace) && (
          <>
            <br />
            {birthdate && <>Born {birthdate}</>}
            {birthplace && <> · {birthplace}</>}
          </>
        )}
      </p>

      <section className="br-personnel-detail-section">
        <h4 className="br-personnel-detail-section-title">Stats</h4>
        <dl className="br-personnel-detail-stats">
          {STAT_ORDER.map((s) =>
            s.key in stats ? (
              <div key={s.key}>
                <dt>{s.label}</dt>
                <dd className="br-mono">{String(stats[s.key])}</dd>
              </div>
            ) : null,
          )}
        </dl>
      </section>

      {Object.keys(skills).length > 0 && (
        <section className="br-personnel-detail-section">
          <h4 className="br-personnel-detail-section-title">Skills</h4>
          <table className="br-personnel-detail-skills">
            <thead>
              <tr>
                <th>Skill</th>
                <th className="br-mono">SL</th>
                <th className="br-mono">PC</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(skills).map(([name, value]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td className="br-mono">{value.skill_level}</td>
                  <td className="br-mono">{value.primary_chance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {abilities.length > 0 && (
        <section className="br-personnel-detail-section">
          <h4 className="br-personnel-detail-section-title">Abilities</h4>
          <ul className="br-personnel-detail-chip-list">
            {abilities.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </section>
      )}

      {Object.keys(physical).length > 0 && (
        <section className="br-personnel-detail-section">
          <h4 className="br-personnel-detail-section-title">Physical</h4>
          <dl className="br-personnel-detail-physical">
            {PHYSICAL_ORDER.map((p) => {
              const val = physical[p.key];
              if (val === null || val === undefined) return null;
              return (
                <div key={p.key}>
                  <dt>{p.label}</dt>
                  <dd className="br-mono">{String(val)}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      )}

      {(famePoints !== undefined || heroPoints !== undefined) && (
        <section className="br-personnel-detail-section">
          <h4 className="br-personnel-detail-section-title">Points</h4>
          <dl className="br-personnel-detail-points">
            {famePoints !== undefined && (
              <div>
                <dt>Fame</dt>
                <dd className="br-mono">{famePoints}</dd>
              </div>
            )}
            {heroPoints !== undefined && (
              <div>
                <dt>Hero</dt>
                <dd className="br-mono">{heroPoints}</dd>
              </div>
            )}
          </dl>
        </section>
      )}

      {fields.length > 0 && (
        <section className="br-personnel-detail-section">
          <h4 className="br-personnel-detail-section-title">Fields of Experience</h4>
          <ul className="br-personnel-detail-chip-list">
            {fields.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </section>
      )}

      {background && (
        <section className="br-personnel-detail-section">
          <h4 className="br-personnel-detail-section-title">Background</h4>
          <p
            className="br-personnel-detail-prose"
            data-testid={`${testidPrefix}-detail-background`}
          >
            {background}
          </p>
        </section>
      )}

      {personality && (
        <section className="br-personnel-detail-section">
          <h4 className="br-personnel-detail-section-title">Personality</h4>
          <p
            className="br-personnel-detail-prose"
            data-testid={`${testidPrefix}-detail-personality`}
          >
            {personality}
          </p>
        </section>
      )}

      {relationships && (
        <section className="br-personnel-detail-section">
          <h4 className="br-personnel-detail-section-title">Relationships</h4>
          <p
            className="br-personnel-detail-prose"
            data-testid={`${testidPrefix}-detail-relationships`}
          >
            {relationships}
          </p>
        </section>
      )}

      {gameInformation && (
        <section className="br-personnel-detail-section">
          <h4 className="br-personnel-detail-section-title">Game Info</h4>
          <p
            className="br-personnel-detail-prose br-personnel-detail-game"
            data-testid={`${testidPrefix}-detail-game-info`}
          >
            {gameInformation}
          </p>
        </section>
      )}
    </div>
  );
}

export function QBranchPersonnelDrawer({
  personnel,
  state,
  error,
  activeTab,
  onTabChange,
  selectedPersonnelId,
  selectedPersonnelData,
  selectedPersonnelState,
  onSelectPersonnel,
  onClose,
  testidPrefix = 'br-personnel-library',
}: QBranchPersonnelDrawerProps) {
  const targetCategory = TAB_TO_CATEGORY[activeTab];
  const tabPersonnel = personnel.filter((p) => p.category === targetCategory);

  return (
    <aside
      className="br-scenario-drawer br-personnel-library-drawer"
      data-testid={testidPrefix}
      data-state={state}
      data-active-tab={activeTab}
    >
      <header className="br-personnel-library-header">
        <span className="br-personnel-library-title">Q Branch Personnel</span>
        <span
          className="br-personnel-library-count br-label"
          data-testid={`${testidPrefix}-count`}
        >
          {tabPersonnel.length}
        </span>
        {onClose !== undefined && (
          <button
            type="button"
            className="br-personnel-library-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close personnel library"
          >
            ✕
          </button>
        )}
      </header>

      <div
        className="br-personnel-library-tabs"
        role="tablist"
        data-testid={`${testidPrefix}-tabs`}
      >
        {TAB_DEFS.map((tabDef) => {
          const isActive = tabDef.key === activeTab;
          return (
            <button
              key={tabDef.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`${testidPrefix}-tab-${tabDef.key}`}
              data-active={isActive ? 'true' : 'false'}
              className={
                isActive
                  ? 'br-personnel-library-tab br-personnel-library-tab-active'
                  : 'br-personnel-library-tab'
              }
              onClick={() => onTabChange(tabDef.key)}
            >
              {tabDef.label}
            </button>
          );
        })}
      </div>

      <p
        className="br-personnel-library-banner"
        data-testid={`${testidPrefix}-banner`}
      >
        Q Manual VG35002 §18 dossiers — 6 NPCs across Q Branch leadership
        (Boothroyd/Q), the Harrow Club (Cobbett/Maclean/Powell/Roberts),
        and the newer member Dr. Turner. Click any row to expand the full
        dossier (stats, skills, abilities, physical, background, etc.).
      </p>

      {state === 'loading' && (
        <p
          className="br-personnel-library-loading"
          data-testid={`${testidPrefix}-loading`}
        >
          Loading personnel…
        </p>
      )}

      {state === 'error' && (
        <p
          className="br-personnel-library-error"
          data-testid={`${testidPrefix}-error`}
        >
          Failed to load personnel: {error ?? 'unknown error'}
        </p>
      )}

      {state === 'ready' && tabPersonnel.length === 0 && (
        <p
          className="br-personnel-library-empty"
          data-testid={`${testidPrefix}-empty`}
        >
          No personnel in this category.
        </p>
      )}

      {state === 'ready' && tabPersonnel.length > 0 && (
        <div
          className="br-personnel-library-list"
          data-testid={`${testidPrefix}-list`}
        >
          <ul className="br-personnel-library-rows">
            {tabPersonnel.map((p) => {
              const isSelected = p.id === selectedPersonnelId;
              return (
                <li key={p.id} className="br-personnel-library-row-wrapper">
                  <button
                    type="button"
                    className={
                      isSelected
                        ? 'br-personnel-library-row br-personnel-library-row-selected'
                        : 'br-personnel-library-row'
                    }
                    data-testid={`${testidPrefix}-row-${p.id}`}
                    data-selected={isSelected ? 'true' : 'false'}
                    onClick={() => onSelectPersonnel(p.id)}
                    aria-pressed={isSelected}
                  >
                    <span className="br-personnel-library-row-title">
                      {p.name}
                      {p.code_name && (
                        <span className="br-personnel-library-row-codename">
                          {' '}
                          ({p.code_name})
                        </span>
                      )}
                    </span>
                    {p.role && (
                      <span className="br-personnel-library-row-meta">
                        {p.role}
                      </span>
                    )}
                  </button>
                  {isSelected && selectedPersonnelState === 'loading' && (
                    <p
                      className="br-personnel-library-detail-loading"
                      data-testid={`${testidPrefix}-detail-loading`}
                    >
                      Loading…
                    </p>
                  )}
                  {isSelected && selectedPersonnelState === 'error' && (
                    <p
                      className="br-personnel-library-detail-error"
                      data-testid={`${testidPrefix}-detail-error`}
                    >
                      Could not load personnel.
                    </p>
                  )}
                  {isSelected &&
                    selectedPersonnelState === 'ready' &&
                    selectedPersonnelData !== null && (
                      <PersonnelDetail
                        data={selectedPersonnelData}
                        testidPrefix={testidPrefix}
                      />
                    )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </aside>
  );
}
